import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { Settings, Clock, Save, Loader2, Mail, Bell, FileText, Users, Download, TestTube, Building2, Database, AlertTriangle, CheckCircle2, Upload, FolderDown, FolderUp, File, Trash2, FormInput, History, FolderOpen, Briefcase, Archive, RefreshCw, Play, Server, Globe, ArrowRight, ArrowLeft, Cable, Copy, CheckCircle, Plus, Search, Rocket, ChevronDown, GitCommit, RotateCcw, Code } from "lucide-react";
import { Input } from "../../components/ui/input";
import { useAuth } from "../../contexts/AuthContext";
import DeployModal from "../../components/DeployModal";
import ScoringSettings from "../../components/crm/ScoringSettings";
import ReminderSettingsCard from "../../components/settings/ReminderSettingsCard";
import EmailNotificationCard from "../../components/settings/EmailNotificationCard";
import ManagerReportsCard from "../../components/settings/ManagerReportsCard";
import CrmActivityReportCard from "../../components/settings/CrmActivityReportCard";
import DataCleanupCard from "../../components/settings/DataCleanupCard";
import BackupRestoreCard from "../../components/settings/BackupRestoreCard";
import AdvancedToolsCard from "../../components/settings/AdvancedToolsCard";
import HolidaySettingsCard from "../../components/settings/HolidaySettingsCard";
import BulkResearchCard from "../../components/settings/BulkResearchCard";
import OwnershipSyncCard from "../../components/settings/OwnershipSyncCard";
import { CompanyIntelligenceCard } from "../../components/settings/CompanyIntelligenceCard";
import EmailProviderSettingsCard from "../../components/settings/EmailProviderSettingsCard";

export default function SettingsPage() {
  const { user } = useAuth();
  const isTaj = user?.email === "taj@jobtalk.ai";
  const [loading, setLoading] = useState(true);
  const [savingReminders, setSavingReminders] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [savingTeamLeaderReport, setSavingTeamLeaderReport] = useState(false);

  // Deploy modal
  const [deployModalOpen, setDeployModalOpen] = useState(false);

  // Deploy to Prod
  const [deployProdConfirmOpen, setDeployProdConfirmOpen] = useState(false);
  const [deployingProd, setDeployingProd] = useState(false);
  const [deployProdSteps, setDeployProdSteps] = useState([]);
  const [deployPreview, setDeployPreview] = useState(null);
  const [loadingPreviewDeploy, setLoadingPreviewDeploy] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  
  // Reminder time settings
  const [todayReminderTime, setTodayReminderTime] = useState("18:00");
  const [finalReminderTime, setFinalReminderTime] = useState("10:00");
  const [todayReminderEnabled, setTodayReminderEnabled] = useState(true);
  const [finalReminderEnabled, setFinalReminderEnabled] = useState(true);
  
  // Email notification settings
  const [ccRemindersToTeamLeader, setCcRemindersToTeamLeader] = useState(false);
  const [ccRemindersToAdmin, setCcRemindersToAdmin] = useState(false);
  const [ccRemindersToTaj, setCcRemindersToTaj] = useState(false);
  const [notifyTeamLeaderOnSubmission, setNotifyTeamLeaderOnSubmission] = useState(false);
  const [notifyAdminOnSubmission, setNotifyAdminOnSubmission] = useState(false);
  const [notifyTajOnSubmission, setNotifyTajOnSubmission] = useState(false);
  const [productivityThreshold, setProductivityThreshold] = useState("");
  const [productivityAlertEmail, setProductivityAlertEmail] = useState("taj@jobtalk.ai");
  
  // Team Leader Report settings
  const [teamLeaderReportEnabled, setTeamLeaderReportEnabled] = useState(false);
  const [teamLeaderReportTime, setTeamLeaderReportTime] = useState("09:00");
  const [includeTasksToday, setIncludeTasksToday] = useState(true);
  const [includeChallenges, setIncludeChallenges] = useState(true);
  const [includePlanTomorrow, setIncludePlanTomorrow] = useState(true);
  const [includeAiDiscovery, setIncludeAiDiscovery] = useState(true);
  
  // CRM Activity Report settings
  const [crmActivityReportEnabled, setCrmActivityReportEnabled] = useState(true);
  const [crmActivityReportRecipients, setCrmActivityReportRecipients] = useState(["taj@jobtalk.ai", "preatha@jobtalk.ai"]);
  const [savingCrmActivityReport, setSavingCrmActivityReport] = useState(false);

  // Test Report Dialog
  const [testReportDialogOpen, setTestReportDialogOpen] = useState(false);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [selectedTeamLeaders, setSelectedTeamLeaders] = useState([]);
  const [loadingTeamLeaders, setLoadingTeamLeaders] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [sendReportToLeaders, setSendReportToLeaders] = useState(false);
  
  // Export/Import
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedImportFiles, setSelectedImportFiles] = useState({});
  const [importResult, setImportResult] = useState(null);
  const [combinedBackupFile, setCombinedBackupFile] = useState(null);
  const [selectedFolderName, setSelectedFolderName] = useState("");
  const [folderFiles, setFolderFiles] = useState([]);
  const combinedFileInputRef = useRef(null);
  
  // CRM Export/Import
  const [crmExportDialogOpen, setCrmExportDialogOpen] = useState(false);
  const [crmImportDialogOpen, setCrmImportDialogOpen] = useState(false);
  const [crmExporting, setCrmExporting] = useState(false);
  const [crmImporting, setCrmImporting] = useState(false);
  const [crmBackupFile, setCrmBackupFile] = useState(null);
  const [crmImportResult, setCrmImportResult] = useState(null);
  const crmFileInputRef = useRef(null);
  
  // New Unified Export/Import ALL
  const [exportingAll, setExportingAll] = useState(false);
  const [exportingEnv, setExportingEnv] = useState(false);
  const [importAllDialogOpen, setImportAllDialogOpen] = useState(false);
  const [importingAll, setImportingAll] = useState(false);
  const [importAllFile, setImportAllFile] = useState(null);
  const [importAllResult, setImportAllResult] = useState(null);
  const [showImportWarning, setShowImportWarning] = useState(false);
  const [exportResultDialogOpen, setExportResultDialogOpen] = useState(false);
  const [exportResultData, setExportResultData] = useState(null);
  const [importProgress, setImportProgress] = useState(null); // { collection, percent, index, total, records }
  const [importPreview, setImportPreview] = useState(null); // preview data from backend
  const [loadingImportPreview, setLoadingImportPreview] = useState(false);

  // Forms Export/Import
  const [exportingForms, setExportingForms] = useState(false);
  const [importFormsDialogOpen, setImportFormsDialogOpen] = useState(false);
  const [importingForms, setImportingForms] = useState(false);
  const [importFormsFile, setImportFormsFile] = useState(null);
  const [importFormsResult, setImportFormsResult] = useState(null);

  
  // Database Migration
  const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);
  const [exportingMigration, setExportingMigration] = useState(false);
  const [migrationStep, setMigrationStep] = useState(1); // 1: Choose, 2: Export/Import
  const [migrationMode, setMigrationMode] = useState(null); // 'export' or 'import'
  const [newDbConnectionString, setNewDbConnectionString] = useState('');
  const [newDbName, setNewDbName] = useState('');
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState(null);
  const [migrationFile, setMigrationFile] = useState(null);
  const [migratingData, setMigratingData] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [importingFromMP, setImportingFromMP] = useState(false);
  const [importFromMPResult, setImportFromMPResult] = useState(null);
  const migrationFileInputRef = useRef(null);
  
  // Automatic Backup Settings
  const [backupSettings, setBackupSettings] = useState({
    enabled: false,
    frequency: 'daily',
    retention_days: 13,
    backup_time: '02:00'
  });
  const [backupSettingsLoading, setBackupSettingsLoading] = useState(true);
  const [savingBackupSettings, setSavingBackupSettings] = useState(false);
  const [backupList, setBackupList] = useState([]);
  const [backupListLoading, setBackupListLoading] = useState(false);
  const [runningBackup, setRunningBackup] = useState(false);

  // Bulk Delete state
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [deleteConditions, setDeleteConditions] = useState([
    { field: 'work_email', operator: 'is_null', value: '' }
  ]);
  const [deleteCompaniesWithNoLeads, setDeleteCompaniesWithNoLeads] = useState(false);
  const [deletingLeads, setDeletingLeads] = useState(false);
  const [deletePreviewCount, setDeletePreviewCount] = useState(null);
  const [deletePreviewLeads, setDeletePreviewLeads] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Smart Bulk Delete state
  const [smartDeletePreview, setSmartDeletePreview] = useState(null);
  const [loadingSmartPreview, setLoadingSmartPreview] = useState(false);
  const [executingSmartDelete, setExecutingSmartDelete] = useState(false);

  // Duplicate Leads state
  const [duplicateLeadsPreview, setDuplicateLeadsPreview] = useState(null);
  const [loadingDuplicateLeadsPreview, setLoadingDuplicateLeadsPreview] = useState(false);
  const [executingDuplicateLeadsDelete, setExecutingDuplicateLeadsDelete] = useState(false);

  // Duplicate Companies state
  const [duplicateCompaniesPreview, setDuplicateCompaniesPreview] = useState(null);
  const [loadingDuplicateCompaniesPreview, setLoadingDuplicateCompaniesPreview] = useState(false);
  const [executingDuplicateCompaniesDelete, setExecutingDuplicateCompaniesDelete] = useState(false);

  // Duplicate Templates state
  const [duplicateTemplatesPreview, setDuplicateTemplatesPreview] = useState(null);
  const [loadingDuplicateTemplatesPreview, setLoadingDuplicateTemplatesPreview] = useState(false);
  const [executingDuplicateTemplatesDelete, setExecutingDuplicateTemplatesDelete] = useState(false);

  // Duplicate Forms state
  const [duplicateFormsPreview, setDuplicateFormsPreview] = useState(null);
  const [loadingDuplicateFormsPreview, setLoadingDuplicateFormsPreview] = useState(false);
  const [executingDuplicateFormsDelete, setExecutingDuplicateFormsDelete] = useState(false);

  // Selective Export/Import state
  const [selectiveExportDialogOpen, setSelectiveExportDialogOpen] = useState(false);
  const [selectiveImportDialogOpen, setSelectiveImportDialogOpen] = useState(false);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [AVAILABLE_COLLECTIONS, setAvailableCollections] = useState([]);
  const [exportingSelected, setExportingSelected] = useState(false);
  const [importingSelected, setImportingSelected] = useState(false);
  const [selectiveImportFile, setSelectiveImportFile] = useState(null);
  const [selectiveImportResult, setSelectiveImportResult] = useState(null);
  const selectiveImportFileRef = useRef(null);
  
  // Sync Database state
  const [syncDatabaseDialogOpen, setSyncDatabaseDialogOpen] = useState(false);
  const [syncMode, setSyncMode] = useState('merge'); // 'merge' or 'override'
  const [syncConnectionString, setSyncConnectionString] = useState('');
  const [syncDatabaseName, setSyncDatabaseName] = useState('');
  const [testingSyncConnection, setTestingSyncConnection] = useState(false);
  const [syncConnectionTestResult, setSyncConnectionTestResult] = useState(null);
  const [syncingDatabase, setSyncingDatabase] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  
  // Environment Variables
  const [envVarsDialogOpen, setEnvVarsDialogOpen] = useState(false);
  const [envVars, setEnvVars] = useState({ backend: {}, frontend: {} });
  const [loadingEnvVars, setLoadingEnvVars] = useState(false);
  const [savingEnvVars, setSavingEnvVars] = useState(false);
  const [activeEnvTab, setActiveEnvTab] = useState('backend');
  const [editedEnvVars, setEditedEnvVars] = useState({ backend: {}, frontend: {} });
  
  const fileInputRefs = {
    users: useRef(null),
    daily_logs: useRef(null),
    teams: useRef(null),
    forms: useRef(null),
    invites: useRef(null),
    email_logs: useRef(null),
    settings: useRef(null)
  };

  useEffect(() => {
    fetchSettings();
    fetchBackupSettings();
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/backup/collections`, { withCredentials: true });
      const iconMap = { users: Users, teams: Building2, daily_logs: FileText, forms: FormInput, leads: Users, companies: Building2, email_logs: History, settings: Settings, crm_communications: Mail, crm_templates: File, email_accounts: Mail, hr_data: Users };
      const cols = res.data.map(c => ({ ...c, icon: iconMap[c.id] || Database }));
      setAvailableCollections(cols);
    } catch (err) {
      console.error("Failed to fetch collections:", err);
    }
  };

  const fetchSettings = async () => {
    try {
      const [remindersRes, notificationsRes, teamLeaderReportRes, crmActivityRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/reminders`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/email-notifications`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/team-leader-report`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/crm-activity-report`, { withCredentials: true }).catch(() => null),
      ]);

      setTodayReminderTime(remindersRes.data.today_reminder_time);
      setFinalReminderTime(remindersRes.data.final_reminder_time);
      setTodayReminderEnabled(remindersRes.data.today_reminder_enabled !== false);
      setFinalReminderEnabled(remindersRes.data.final_reminder_enabled !== false);

      setCcRemindersToTeamLeader(notificationsRes.data.cc_reminders_to_team_leader);
      setCcRemindersToAdmin(notificationsRes.data.cc_reminders_to_admin);
      setCcRemindersToTaj(notificationsRes.data.cc_reminders_to_taj || false);
      setNotifyTeamLeaderOnSubmission(notificationsRes.data.notify_team_leader_on_submission);
      setNotifyAdminOnSubmission(notificationsRes.data.notify_admin_on_submission);
      setNotifyTajOnSubmission(notificationsRes.data.notify_taj_on_submission || false);
      setProductivityThreshold(notificationsRes.data.productivity_threshold || "");
      setProductivityAlertEmail(notificationsRes.data.productivity_alert_email || "taj@jobtalk.ai");

      setTeamLeaderReportEnabled(teamLeaderReportRes.data.enabled);
      setTeamLeaderReportTime(teamLeaderReportRes.data.report_time);
      setIncludeTasksToday(teamLeaderReportRes.data.include_tasks_today);
      setIncludeChallenges(teamLeaderReportRes.data.include_challenges);
      setIncludePlanTomorrow(teamLeaderReportRes.data.include_plan_tomorrow);
      setIncludeAiDiscovery(teamLeaderReportRes.data.include_ai_discovery ?? true);

      if (crmActivityRes) {
        setCrmActivityReportEnabled(crmActivityRes.data.enabled ?? true);
        setCrmActivityReportRecipients(crmActivityRes.data.recipients || ["taj@jobtalk.ai", "preatha@jobtalk.ai"]);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  // Backup Settings Functions
  const fetchBackupSettings = async () => {
    setBackupSettingsLoading(true);
    try {
      const [settingsRes, listRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/backup/settings`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/backup/list`, { withCredentials: true })
      ]);
      setBackupSettings(settingsRes.data);
      setBackupList(listRes.data.backups || []);
    } catch (error) {
      console.error("Error fetching backup settings:", error);
    } finally {
      setBackupSettingsLoading(false);
    }
  };

  const handleSaveBackupSettings = async () => {
    setSavingBackupSettings(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/settings`,
        backupSettings,
        { withCredentials: true }
      );
      toast.success("Backup settings saved successfully");
    } catch (error) {
      console.error("Error saving backup settings:", error);
      toast.error("Failed to save backup settings");
    } finally {
      setSavingBackupSettings(false);
    }
  };

  const handleRunBackupNow = async () => {
    setRunningBackup(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/run-now`,
        {},
        { withCredentials: true }
      );
      toast.success(`Backup created: ${response.data.backup.filename}`);
      fetchBackupSettings(); // Refresh backup list
    } catch (error) {
      console.error("Error running backup:", error);
      toast.error(error.response?.data?.detail || "Failed to create backup");
    } finally {
      setRunningBackup(false);
    }
  };

  const handleDownloadBackup = async (filename) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/download/${filename}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      console.error("Error downloading backup:", error);
      toast.error("Failed to download backup");
    }
  };

  const handleDeleteBackup = async (filename) => {
    if (!window.confirm(`Delete backup "${filename}"? This cannot be undone.`)) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/${filename}`,
        { withCredentials: true }
      );
      toast.success(`Deleted ${filename}`);
      fetchBackupSettings(); // Refresh backup list
    } catch (error) {
      console.error("Error deleting backup:", error);
      toast.error("Failed to delete backup");
    }
  };

  // Environment Variables Functions
  const fetchEnvVars = async () => {
    setLoadingEnvVars(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/env-vars`,
        { withCredentials: true }
      );
      const data = response.data;
      setEnvVars({
        backend: data.backend?.variables || {},
        frontend: data.frontend?.variables || {}
      });
      setEditedEnvVars({
        backend: { ...data.backend?.variables } || {},
        frontend: { ...data.frontend?.variables } || {}
      });
    } catch (error) {
      console.error("Error fetching env vars:", error);
      toast.error("Failed to load environment variables");
    } finally {
      setLoadingEnvVars(false);
    }
  };

  const handleOpenEnvVarsDialog = () => {
    setEnvVarsDialogOpen(true);
    fetchEnvVars();
  };

  const handleEnvVarChange = (env, key, value) => {
    setEditedEnvVars(prev => ({
      ...prev,
      [env]: {
        ...prev[env],
        [key]: value
      }
    }));
  };

  const handleSaveEnvVars = async (environment) => {
    setSavingEnvVars(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/env-vars`,
        {
          environment,
          variables: editedEnvVars[environment]
        },
        { withCredentials: true }
      );
      toast.success(`${environment} environment variables saved! Restart required for changes to take effect.`);
      // Update the original values
      setEnvVars(prev => ({
        ...prev,
        [environment]: { ...editedEnvVars[environment] }
      }));
    } catch (error) {
      console.error("Error saving env vars:", error);
      toast.error(error.response?.data?.detail || "Failed to save environment variables");
    } finally {
      setSavingEnvVars(false);
    }
  };

  const hasEnvChanges = (environment) => {
    const original = envVars[environment] || {};
    const edited = editedEnvVars[environment] || {};
    return JSON.stringify(original) !== JSON.stringify(edited);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const handleSaveReminders = async () => {
    setSavingReminders(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/reminders`,
        {
          today_reminder_time: todayReminderTime,
          final_reminder_time: finalReminderTime,
          today_reminder_enabled: todayReminderEnabled,
          final_reminder_enabled: finalReminderEnabled
        },
        { withCredentials: true }
      );
      toast.success("Reminder times saved successfully");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingReminders(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/email-notifications`,
        {
          cc_reminders_to_team_leader: ccRemindersToTeamLeader,
          cc_reminders_to_admin: ccRemindersToAdmin,
          cc_reminders_to_taj: ccRemindersToTaj,
          notify_team_leader_on_submission: notifyTeamLeaderOnSubmission,
          notify_admin_on_submission: notifyAdminOnSubmission,
          notify_taj_on_submission: notifyTajOnSubmission,
          productivity_threshold: productivityThreshold ? parseInt(productivityThreshold) : null,
          productivity_alert_email: productivityAlertEmail
        },
        { withCredentials: true }
      );
      toast.success("Email notification settings saved successfully");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingNotifications(false);
    }
  };

  const handleSaveTeamLeaderReport = async () => {
    setSavingTeamLeaderReport(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/team-leader-report`,
        {
          enabled: teamLeaderReportEnabled,
          report_time: teamLeaderReportTime,
          include_tasks_today: includeTasksToday,
          include_challenges: includeChallenges,
          include_plan_tomorrow: includePlanTomorrow,
          include_ai_discovery: includeAiDiscovery
        },
        { withCredentials: true }
      );
      toast.success("Team Leader Report settings saved successfully");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingTeamLeaderReport(false);
    }
  };

  const handleSaveCrmActivityReport = async () => {
    setSavingCrmActivityReport(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings/crm-activity-report`,
        { enabled: crmActivityReportEnabled, recipients: crmActivityReportRecipients },
        { withCredentials: true }
      );
      toast.success("CRM Activity Report settings saved");
    } catch (error) {
      console.error("Error saving CRM activity report settings:", error);
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingCrmActivityReport(false);
    }
  };

  const openTestReportDialog = async () => {
    setTestReportDialogOpen(true);
    setLoadingTeamLeaders(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/team-leaders-with-teams`,
        { withCredentials: true }
      );
      // Sort team leaders alphabetically by name
      const sortedLeaders = response.data.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setTeamLeaders(sortedLeaders);
      // Select all by default
      setSelectedTeamLeaders(sortedLeaders.map(tl => tl.id));
    } catch (error) {
      console.error("Error fetching team leaders:", error);
      toast.error("Failed to load team leaders");
    } finally {
      setLoadingTeamLeaders(false);
    }
  };

  const toggleTeamLeaderSelection = (leaderId) => {
    setSelectedTeamLeaders(prev => 
      prev.includes(leaderId)
        ? prev.filter(id => id !== leaderId)
        : [...prev, leaderId]
    );
  };

  const selectAllTeamLeaders = () => {
    setSelectedTeamLeaders(teamLeaders.map(tl => tl.id));
  };

  const deselectAllTeamLeaders = () => {
    setSelectedTeamLeaders([]);
  };

  const handleGenerateTestReport = async () => {
    if (selectedTeamLeaders.length === 0) {
      toast.error("Please select at least one team leader");
      return;
    }
    
    if (!includeTasksToday && !includeChallenges && !includePlanTomorrow) {
      toast.error("Please select at least one report content option above");
      return;
    }

    setGeneratingReport(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/team-leader-report/test`,
        {
          team_leader_ids: selectedTeamLeaders,
          include_tasks_today: includeTasksToday,
          include_challenges: includeChallenges,
          include_plan_tomorrow: includePlanTomorrow,
          include_ai_discovery: includeAiDiscovery,
          send_email_to_leaders: sendReportToLeaders
        },
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `team_leader_report_test_${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      
      if (sendReportToLeaders) {
        toast.success("Test report generated, downloaded, and emailed to team leaders!");
      } else {
        toast.success("Test report generated and downloaded!");
      }
      setTestReportDialogOpen(false);
      setSendReportToLeaders(false); // Reset checkbox
    } catch (error) {
      console.error("Error generating test report:", error);
      toast.error(error.response?.data?.detail || "Failed to generate test report");
    } finally {
      setGeneratingReport(false);
    }
  };

  // Export all tables as a SINGLE combined backup file (recommended for easy import)
  const handleExportCombined = async () => {
    setExporting(true);
    const exportCollections = AVAILABLE_COLLECTIONS.map(c => c.id);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `dailylog_full_backup_${timestamp}.json`;
    
    try {
      const combinedBackup = {
        backup_type: "combined",
        created_at: new Date().toISOString(),
        version: "1.0",
        collections: {}
      };

      for (const collection of exportCollections) {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/export/${collection}`,
          { withCredentials: true }
        );
        combinedBackup.collections[collection] = response.data;
      }
      
      const blob = new Blob([JSON.stringify(combinedBackup, null, 2)], { type: 'application/json' });
      
      // Try to use File System Access API to let user choose folder
      if ('showSaveFilePicker' in window) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'JSON Files',
              accept: { 'application/json': ['.json'] }
            }]
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          toast.success(`Backup saved successfully!`);
          setExportDialogOpen(false);
          return;
        } catch (err) {
          // User cancelled or API not fully supported, fall back to download
          if (err.name !== 'AbortError') {
            console.log('File System API not available, using download fallback');
          } else {
            setExporting(false);
            return; // User cancelled
          }
        }
      }
      
      // Fallback: Download to default location
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success("Full backup exported successfully! Use this file for one-click import.");
      setExportDialogOpen(false);
    } catch (error) {
      console.error("Export error:", error);
      toast.error(error.response?.data?.detail || "Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  // Export all tables as separate JSON files
  const handleExportAll = async () => {
    setExporting(true);
    const exportCollections = AVAILABLE_COLLECTIONS.map(c => c.id);
    const timestamp = new Date().toISOString().split('T')[0];
    
    try {
      // Try to use File System Access API to let user choose folder
      if ('showDirectoryPicker' in window) {
        try {
          const dirHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
          
          for (const collection of exportCollections) {
            const response = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/admin/export/${collection}`,
              { withCredentials: true }
            );
            
            const filename = `${collection}_backup_${timestamp}.json`;
            const fileHandle = await dirHandle.getFileHandle(filename, { create: true });
            const writable = await fileHandle.createWritable();
            await writable.write(JSON.stringify(response.data, null, 2));
            await writable.close();
          }
          
          toast.success(`All tables exported to selected folder!`);
          setExportDialogOpen(false);
          return;
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.log('Directory picker not available, using download fallback');
          } else {
            setExporting(false);
            return; // User cancelled
          }
        }
      }
      
      // Fallback: Download each file separately
      for (const collection of exportCollections) {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/export/${collection}`,
          { withCredentials: true }
        );
        
        // Create and download JSON file
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${collection}_backup_${timestamp}.json`;
        link.click();
        URL.revokeObjectURL(url);
        
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      toast.success("All tables exported as separate files!");
      setExportDialogOpen(false);
    } catch (error) {
      console.error("Export error:", error);
      toast.error(error.response?.data?.detail || "Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  // Export single table
  const handleExportTable = async (collection) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/export/${collection}`,
        { withCredentials: true }
      );
      
      const timestamp = new Date().toISOString().split('T')[0];
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${collection}_backup_${timestamp}.json`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success(`${collection} exported successfully!`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error(`Failed to export ${collection}`);
    }
  };

  // Handle file selection for import
  const handleFileSelect = (collection, event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImportFiles(prev => ({ ...prev, [collection]: file }));
    }
  };

  // Clear selected file
  const clearSelectedFile = (collection) => {
    setSelectedImportFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[collection];
      return newFiles;
    });
    if (fileInputRefs[collection]?.current) {
      fileInputRefs[collection].current.value = '';
    }
  };

  // Handle combined backup file selection
  const handleCombinedFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCombinedBackupFile(file);
    }
  };

  // Select folder and find backup files using File System Access API
  const handleSelectFolder = async () => {
    if (!('showDirectoryPicker' in window)) {
      toast.error("Folder selection is not supported in this browser. Please use Chrome or Edge.");
      return;
    }

    try {
      const dirHandle = await window.showDirectoryPicker({ mode: 'read' });
      setSelectedFolderName(dirHandle.name);
      
      const foundFiles = [];
      
      // Scan for JSON backup files
      for await (const entry of dirHandle.values()) {
        if (entry.kind === 'file' && entry.name.endsWith('.json')) {
          const file = await entry.getFile();
          const content = await file.text();
          
          try {
            const data = JSON.parse(content);
            // Check if it's a combined backup
            if (data.backup_type === "combined") {
              foundFiles.push({
                type: 'combined',
                name: entry.name,
                file: file,
                created_at: data.created_at,
                collections: Object.keys(data.collections || {})
              });
            } 
            // Check if it's an individual table backup
            else if (Array.isArray(data) || (data.data && Array.isArray(data.data))) {
              // Try to determine which collection it is from filename
              const collectionMatch = entry.name.match(/^(users|daily_logs|teams|forms|invites|email_logs|settings)_backup/);
              if (collectionMatch) {
                foundFiles.push({
                  type: 'individual',
                  collection: collectionMatch[1],
                  name: entry.name,
                  file: file
                });
              }
            }
          } catch (e) {
            // Not a valid JSON or not a backup file, skip
          }
        }
      }
      
      setFolderFiles(foundFiles);
      
      if (foundFiles.length === 0) {
        toast.info("No backup files found in selected folder");
      } else {
        // Auto-select combined backup if found
        const combinedFile = foundFiles.find(f => f.type === 'combined');
        if (combinedFile) {
          setCombinedBackupFile(combinedFile.file);
          toast.success(`Found combined backup: ${combinedFile.name}`);
        } else {
          toast.success(`Found ${foundFiles.length} backup file(s) in folder`);
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error("Folder selection error:", err);
        toast.error("Failed to access folder");
      }
    }
  };

  // Import from combined backup file (one-click import all)
  const handleImportCombined = async () => {
    if (!combinedBackupFile) {
      toast.error("Please select a backup file first");
      return;
    }

    setImporting(true);
    setImportResult(null);
    const results = {};

    try {
      const fileContent = await combinedBackupFile.text();
      const backupData = JSON.parse(fileContent);
      
      // Validate it's a combined backup
      if (backupData.backup_type !== "combined" || !backupData.collections) {
        toast.error("Invalid backup file. Please select a combined backup file (dailylog_full_backup_*.json)");
        setImporting(false);
        return;
      }

      // Import each collection
      for (const [collection, collectionData] of Object.entries(backupData.collections)) {
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/admin/import/${collection}`,
            { data: collectionData },
            { withCredentials: true }
          );
          results[collection] = { success: true, count: response.data.imported_count };
        } catch (err) {
          results[collection] = { success: false, error: err.response?.data?.detail || err.message };
        }
      }
      
      setImportResult(results);
      
      const successCount = Object.values(results).filter(r => r.success).length;
      const totalCount = Object.keys(results).length;
      
      if (successCount === totalCount) {
        toast.success(`All ${totalCount} tables imported successfully!`);
      } else if (successCount > 0) {
        toast.warning(`${successCount}/${totalCount} tables imported with some errors`);
      } else {
        toast.error("Import failed");
      }
      
      // Clear the file selection
      setCombinedBackupFile(null);
      if (combinedFileInputRef.current) {
        combinedFileInputRef.current.value = '';
      }
    } catch (error) {
      console.error("Import error:", error);
      if (error instanceof SyntaxError) {
        toast.error("Invalid JSON file. Please select a valid backup file.");
      } else {
        toast.error("Import failed: " + error.message);
      }
    } finally {
      setImporting(false);
    }
  };

  // Import selected files (individual tables)
  const handleImportFiles = async () => {
    const filesToImport = Object.entries(selectedImportFiles);
    if (filesToImport.length === 0) {
      toast.error("Please select at least one file to import");
      return;
    }

    setImporting(true);
    setImportResult(null);
    const results = {};

    try {
      for (const [collection, file] of filesToImport) {
        try {
          const fileContent = await file.text();
          const jsonData = JSON.parse(fileContent);
          
          const response = await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/admin/import/${collection}`,
            { data: jsonData },
            { withCredentials: true }
          );
          
          results[collection] = { success: true, count: response.data.imported_count };
        } catch (err) {
          results[collection] = { success: false, error: err.response?.data?.detail || err.message };
        }
      }
      
      setImportResult(results);
      
      const successCount = Object.values(results).filter(r => r.success).length;
      if (successCount === filesToImport.length) {
        toast.success("All files imported successfully!");
      } else if (successCount > 0) {
        toast.warning("Some files imported with errors");
      } else {
        toast.error("Import failed");
      }
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };


  // Bulk Delete Functions
  const LEAD_FIELDS_FOR_FILTER = [
    { value: 'work_email', label: 'Work Email' },
    { value: 'personal_email', label: 'Personal Email' },
    { value: 'work_direct_phone', label: 'Work Phone' },
    { value: 'mobile_phone', label: 'Mobile Phone' },
    { value: 'linkedin_url', label: 'LinkedIn URL' },
    { value: 'x_twitter_url', label: 'X/Twitter URL' },
    { value: 'linkedin_url_x', label: 'LinkedIn URL-X' },
    { value: 'first_name', label: 'First Name' },
    { value: 'last_name', label: 'Last Name' },
    { value: 'title', label: 'Title' },
    { value: 'company_name', label: 'Company Name' },
    { value: 'status', label: 'Status' },
    { value: 'source', label: 'Source' },
    { value: 'batch_name', label: 'Batch Name' },
    { value: 'dnc', label: 'Do Not Call' },
    { value: 'dne', label: 'Do Not Email' },
    { value: 'created_at', label: 'Created Date' },
    { value: 'updated_at', label: 'Updated Date' }
  ];

  const OPERATORS = [
    { value: 'is_null', label: 'Is NULL or Blank' },
    { value: 'is_not_null', label: 'Is NOT NULL' },
    { value: 'equals', label: '= (equals)' },
    { value: 'not_equals', label: '<> (not equals)' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does Not Contain' }
  ];

  const addCondition = () => {
    setDeleteConditions([...deleteConditions, { field: 'work_email', operator: 'is_null', value: '' }]);
  };

  const removeCondition = (index) => {
    if (deleteConditions.length === 1) {
      toast.error('At least one condition is required');
      return;
    }
    setDeleteConditions(deleteConditions.filter((_, i) => i !== index));
  };

  const updateCondition = (index, key, value) => {
    const updated = [...deleteConditions];
    updated[index][key] = value;
    setDeleteConditions(updated);
  };

  const handlePreviewDelete = async () => {
    setLoadingPreview(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/bulk-delete/preview`,
        { conditions: deleteConditions },
        { withCredentials: true }
      );
      setDeletePreviewCount(response.data.count);
      setDeletePreviewLeads(response.data.sample_leads || []);
      toast.success(`Found ${response.data.count} leads matching criteria`);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.response?.data?.detail || 'Failed to preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!deletePreviewCount || deletePreviewCount === 0) {
      toast.error('No leads to delete. Run preview first.');
      return;
    }

    if (!window.confirm(`⚠️ WARNING: This will DELETE ${deletePreviewCount} leads permanently. This action cannot be undone. Continue?`)) {
      return;
    }

    setDeletingLeads(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/bulk-delete/leads`,
        { 
          conditions: deleteConditions,
          delete_orphaned_companies: deleteCompaniesWithNoLeads
        },
        { withCredentials: true }
      );
      
      toast.success(`Deleted ${response.data.leads_deleted} leads and ${response.data.companies_deleted} orphaned companies`);
      setBulkDeleteDialogOpen(false);
      setDeleteConditions([{ field: 'work_email', operator: 'is_null', value: '' }]);
      setDeletePreviewCount(null);
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete leads');
    } finally {
      setDeletingLeads(false);
    }
  };

  const handleDeleteCompaniesWithNoLeads = async () => {
    if (!window.confirm('⚠️ WARNING: This will DELETE all companies that have NO leads. This action cannot be undone. Continue?')) {
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/delete-empty-companies`,
        {},
        { withCredentials: true }
      );
      toast.success(`Deleted ${response.data.deleted} companies with no leads`);
    } catch (error) {
      console.error('Delete empty companies error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete companies');
    }
  };

  // Smart Bulk Delete Functions
  const handleSmartDeletePreview = async () => {
    setLoadingSmartPreview(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smart-delete/preview`,
        {},
        { withCredentials: true }
      );
      setSmartDeletePreview(response.data);
      toast.success(`Found ${response.data.leads_count} leads with no valid contact info`);
    } catch (error) {
      console.error('Smart delete preview error:', error);
      toast.error(error.response?.data?.detail || 'Failed to preview');
    } finally {
      setLoadingSmartPreview(false);
    }
  };

  const handleSmartDelete = async () => {
    if (!smartDeletePreview || smartDeletePreview.leads_count === 0) {
      toast.error('No leads to delete. Run preview first.');
      return;
    }

    if (!window.confirm(`⚠️ SMART DELETE WARNING\n\nThis will DELETE:\n• ${smartDeletePreview.leads_count} leads with NO valid email AND NO valid phone\n• ${smartDeletePreview.orphaned_companies_count} companies that will have no remaining leads\n\nThis action cannot be undone. Continue?`)) {
      return;
    }

    setExecutingSmartDelete(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smart-delete/execute`,
        {},
        { withCredentials: true }
      );
      
      toast.success(`Smart Delete Complete: ${response.data.leads_deleted} leads and ${response.data.companies_deleted} companies deleted`);
      setSmartDeletePreview(null);
    } catch (error) {
      console.error('Smart delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to execute smart delete');
    } finally {
      setExecutingSmartDelete(false);
    }
  };

  // Duplicate Leads Functions
  const handleDuplicateLeadsPreview = async () => {
    setLoadingDuplicateLeadsPreview(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smart-delete/duplicate-leads/preview`,
        {},
        { withCredentials: true }
      );
      setDuplicateLeadsPreview(response.data);
      toast.success(`Found ${response.data.leads_to_delete} duplicate leads in ${response.data.duplicate_groups} groups`);
    } catch (error) {
      console.error('Duplicate leads preview error:', error);
      toast.error(error.response?.data?.detail || 'Failed to preview');
    } finally {
      setLoadingDuplicateLeadsPreview(false);
    }
  };

  const handleDuplicateLeadsDelete = async () => {
    if (!duplicateLeadsPreview || duplicateLeadsPreview.leads_to_delete === 0) {
      toast.error('No duplicates to delete. Run preview first.');
      return;
    }
    if (!window.confirm(`⚠️ DUPLICATE LEADS DELETE\n\nThis will DELETE ${duplicateLeadsPreview.leads_to_delete} duplicate leads (keeping the oldest per email).\n\nThis action cannot be undone. Continue?`)) {
      return;
    }
    setExecutingDuplicateLeadsDelete(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smart-delete/duplicate-leads/execute`,
        {},
        { withCredentials: true }
      );
      toast.success(`Deleted ${response.data.leads_deleted} duplicate leads and ${response.data.companies_deleted} orphaned companies`);
      setDuplicateLeadsPreview(null);
    } catch (error) {
      console.error('Duplicate leads delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete duplicates');
    } finally {
      setExecutingDuplicateLeadsDelete(false);
    }
  };

  // Duplicate Companies Functions
  const handleDuplicateCompaniesPreview = async () => {
    setLoadingDuplicateCompaniesPreview(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smart-delete/duplicate-companies/preview`,
        {},
        { withCredentials: true }
      );
      setDuplicateCompaniesPreview(response.data);
      toast.success(`Found ${response.data.companies_to_delete} duplicate companies in ${response.data.duplicate_groups} groups`);
    } catch (error) {
      console.error('Duplicate companies preview error:', error);
      toast.error(error.response?.data?.detail || 'Failed to preview');
    } finally {
      setLoadingDuplicateCompaniesPreview(false);
    }
  };

  const handleDuplicateCompaniesDelete = async () => {
    if (!duplicateCompaniesPreview || duplicateCompaniesPreview.companies_to_delete === 0) {
      toast.error('No duplicates to delete. Run preview first.');
      return;
    }
    if (!window.confirm(`⚠️ DUPLICATE COMPANIES DELETE\n\nThis will DELETE ${duplicateCompaniesPreview.companies_to_delete} duplicate companies (keeping the oldest per name).\nLeads from deleted companies will be reassigned to the kept company.\n\nThis action cannot be undone. Continue?`)) {
      return;
    }
    setExecutingDuplicateCompaniesDelete(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smart-delete/duplicate-companies/execute`,
        {},
        { withCredentials: true }
      );
      toast.success(`Deleted ${response.data.companies_deleted} duplicate companies, reassigned ${response.data.leads_reassigned} leads`);
      setDuplicateCompaniesPreview(null);
    } catch (error) {
      console.error('Duplicate companies delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete duplicates');
    } finally {
      setExecutingDuplicateCompaniesDelete(false);
    }
  };

  // Duplicate Templates Functions
  const handleDuplicateTemplatesPreview = async () => {
    setLoadingDuplicateTemplatesPreview(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smart-delete/duplicate-templates/preview`,
        {},
        { withCredentials: true }
      );
      setDuplicateTemplatesPreview(response.data);
      toast.success(`Found ${response.data.templates_to_delete} duplicate templates in ${response.data.duplicate_groups} groups`);
    } catch (error) {
      console.error('Duplicate templates preview error:', error);
      toast.error(error.response?.data?.detail || 'Failed to preview');
    } finally {
      setLoadingDuplicateTemplatesPreview(false);
    }
  };

  const handleDuplicateTemplatesDelete = async () => {
    if (!duplicateTemplatesPreview || duplicateTemplatesPreview.templates_to_delete === 0) {
      toast.error('No duplicates to delete. Run preview first.');
      return;
    }
    if (!window.confirm(`⚠️ DUPLICATE TEMPLATES DELETE\n\nThis will DELETE ${duplicateTemplatesPreview.templates_to_delete} duplicate templates (keeping the oldest per name+type).\n\nThis action cannot be undone. Continue?`)) {
      return;
    }
    setExecutingDuplicateTemplatesDelete(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smart-delete/duplicate-templates/execute`,
        {},
        { withCredentials: true }
      );
      toast.success(`Deleted ${response.data.templates_deleted} duplicate templates`);
      setDuplicateTemplatesPreview(null);
    } catch (error) {
      console.error('Duplicate templates delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete duplicates');
    } finally {
      setExecutingDuplicateTemplatesDelete(false);
    }
  };

  // Duplicate Forms Functions
  const handleDuplicateFormsPreview = async () => {
    setLoadingDuplicateFormsPreview(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smart-delete/duplicate-forms/preview`,
        {},
        { withCredentials: true }
      );
      setDuplicateFormsPreview(response.data);
      toast.success(`Found ${response.data.forms_to_delete} duplicate forms in ${response.data.duplicate_groups} groups`);
    } catch (error) {
      console.error('Duplicate forms preview error:', error);
      toast.error(error.response?.data?.detail || 'Failed to preview');
    } finally {
      setLoadingDuplicateFormsPreview(false);
    }
  };

  const handleDuplicateFormsDelete = async () => {
    if (!duplicateFormsPreview || duplicateFormsPreview.forms_to_delete === 0) {
      toast.error('No duplicates to delete. Run preview first.');
      return;
    }
    if (!window.confirm(`⚠️ DUPLICATE FORMS DELETE\n\nThis will DELETE ${duplicateFormsPreview.forms_to_delete} duplicate forms (keeping the oldest per name).\nUsers assigned to deleted forms will be reassigned to the kept form.\n\nThis action cannot be undone. Continue?`)) {
      return;
    }
    setExecutingDuplicateFormsDelete(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smart-delete/duplicate-forms/execute`,
        {},
        { withCredentials: true }
      );
      toast.success(`Deleted ${response.data.forms_deleted} duplicate forms${response.data.users_reassigned > 0 ? `, ${response.data.users_reassigned} users reassigned` : ''}`);
      setDuplicateFormsPreview(null);
    } catch (error) {
      console.error('Duplicate forms delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete duplicates');
    } finally {
      setExecutingDuplicateFormsDelete(false);
    }
  };

  // Selective Export/Import Functions (AVAILABLE_COLLECTIONS loaded dynamically from /api/backup/collections)

  const toggleCollection = (collectionId) => {
    setSelectedCollections(prev => 
      prev.includes(collectionId) 
        ? prev.filter(id => id !== collectionId)
        : [...prev, collectionId]
    );
  };

  const selectAllCollections = () => {
    setSelectedCollections(AVAILABLE_COLLECTIONS.map(c => c.id));
  };

  const deselectAllCollections = () => {
    setSelectedCollections([]);
  };

  const handleExportSelected = async () => {
    if (selectedCollections.length === 0) {
      toast.error('Please select at least one collection to export');
      return;
    }

    setExportingSelected(true);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `selected_backup_${timestamp}.json`;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/export-selected`,
        { collections: selectedCollections },
        { withCredentials: true }
      );

      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);

      toast.success(`Exported ${selectedCollections.length} collections successfully!`);
      setSelectiveExportDialogOpen(false);
      setSelectedCollections([]);
    } catch (error) {
      console.error('Export selected error:', error);
      toast.error(error.response?.data?.detail || 'Failed to export selected collections');
    } finally {
      setExportingSelected(false);
    }
  };

  const handleImportSelected = async () => {
    if (!selectiveImportFile) {
      toast.error('Please select a file to import');
      return;
    }

    setImportingSelected(true);

    try {
      const fileContent = await selectiveImportFile.text();
      const data = JSON.parse(fileContent);

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/import-selected`,
        { data },
        { withCredentials: true }
      );

      setSelectiveImportResult(response.data);
      toast.success('Import completed!');
    } catch (error) {
      console.error('Import selected error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import');
    } finally {
      setImportingSelected(false);
    }
  };

  // Sync Database Functions
  const handleTestSyncConnection = async () => {
    if (!syncConnectionString || !syncDatabaseName) {
      toast.error('Please enter both connection string and database name');
      return;
    }

    setTestingSyncConnection(true);
    setSyncConnectionTestResult(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/test-connection`,
        {
          connection_string: syncConnectionString,
          database_name: syncDatabaseName
        },
        { withCredentials: true }
      );

      setSyncConnectionTestResult(response.data);
      if (response.data.success) {
        toast.success('Connection successful!');
      } else {
        toast.error('Connection failed');
      }
    } catch (error) {
      console.error('Connection test error:', error);
      setSyncConnectionTestResult({
        success: false,
        message: error.response?.data?.detail || 'Connection test failed'
      });
      toast.error('Connection test failed');
    } finally {
      setTestingSyncConnection(false);
    }
  };

  const handleSyncDatabase = async () => {
    if (!syncConnectionString || !syncDatabaseName) {
      toast.error('Please enter connection details and test connection first');
      return;
    }

    if (!syncConnectionTestResult || !syncConnectionTestResult.success) {
      toast.error('Please test connection successfully before syncing');
      return;
    }

    const confirmMsg = syncMode === 'merge' 
      ? '⚠️ MERGE SYNC: This will add new fields and tables to the target database without overriding existing data. Continue?'
      : '⚠️ OVERRIDE SYNC: This will REPLACE all data in the target database with data from this database. This action cannot be undone. Continue?';

    if (!window.confirm(confirmMsg)) {
      return;
    }

    setSyncingDatabase(true);
    setSyncResult(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/sync-database`,
        {
          connection_string: syncConnectionString,
          database_name: syncDatabaseName,
          sync_mode: syncMode
        },
        { withCredentials: true }
      );

      setSyncResult(response.data);
      toast.success(`Database sync complete! ${response.data.collections_synced} collections synced.`);
    } catch (error) {
      console.error('Database sync error:', error);
      toast.error(error.response?.data?.detail || 'Database sync failed');
    } finally {
      setSyncingDatabase(false);
    }
  };

  // collections list is now dynamic via AVAILABLE_COLLECTIONS state (fetched from /api/backup/collections)

  // CRM Export function
  const handleCrmExport = async () => {
    setCrmExporting(true);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `crm_full_backup_${timestamp}.json`;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/export-crm`,
        { withCredentials: true }
      );
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      
      // Try to use File System Access API
      if ('showSaveFilePicker' in window) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{
              description: 'JSON Files',
              accept: { 'application/json': ['.json'] }
            }]
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          toast.success('CRM backup saved successfully!');
          setCrmExportDialogOpen(false);
          return;
        } catch (err) {
          if (err.name === 'AbortError') {
            setCrmExporting(false);
            return;
          }
        }
      }
      
      // Fallback download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success('CRM data exported successfully!');
      setCrmExportDialogOpen(false);
    } catch (error) {
      console.error("CRM Export error:", error);
      toast.error(error.response?.data?.detail || "Failed to export CRM data");
    } finally {
      setCrmExporting(false);
    }
  };

  // CRM Import function
  const handleCrmImport = async () => {
    if (!crmBackupFile) {
      toast.error("Please select a CRM backup file first");
      return;
    }

    setCrmImporting(true);
    setCrmImportResult(null);

    try {
      const fileContent = await crmBackupFile.text();
      const backupData = JSON.parse(fileContent);
      
      if (backupData.backup_type !== "crm_data") {
        toast.error("Invalid backup file. Please select a CRM backup file (crm_full_backup_*.json)");
        setCrmImporting(false);
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/import-crm`,
        { data: backupData },
        { withCredentials: true }
      );
      
      setCrmImportResult(response.data.results);
      
      const successCount = Object.values(response.data.results).filter(r => r.success).length;
      const totalCount = Object.keys(response.data.results).length;
      
      if (successCount === totalCount) {
        toast.success(`All ${totalCount} CRM tables imported successfully!`);
      } else if (successCount > 0) {
        toast.warning(`${successCount}/${totalCount} tables imported with some errors`);
      } else {
        toast.error("Import failed");
      }
      
      setCrmBackupFile(null);
      if (crmFileInputRef.current) crmFileInputRef.current.value = '';
    } catch (error) {
      console.error("CRM Import error:", error);
      if (error instanceof SyntaxError) {
        toast.error("Invalid JSON file. Please select a valid CRM backup file.");
      } else {
        toast.error(error.response?.data?.detail || "Import failed");
      }
    } finally {
      setCrmImporting(false);
    }
  };

  // ===== DEPLOY TO PROD =====
  const fetchDeployPreview = async () => {
    setLoadingPreviewDeploy(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/deploy/prod/preview`, { withCredentials: true });
      setDeployPreview(res.data);
    } catch (err) {
      toast.error("Failed to load deploy preview");
    } finally {
      setLoadingPreviewDeploy(false);
    }
  };

  const openDeployDialog = () => {
    setDeployProdSteps([]);
    setDeployProdConfirmOpen(true);
    fetchDeployPreview();
  };

  const handleDeployToProd = async () => {
    setDeployingProd(true);
    setDeployProdSteps([{ message: "Starting production deployment...", status: "info" }]);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/deploy/prod`, {}, { withCredentials: true });
      setDeployProdSteps(res.data.steps || []);
      toast.success("Deployed to production successfully");
      fetchDeployPreview();
    } catch (err) {
      const steps = err.response?.data?.steps || [];
      steps.push({ message: err.response?.data?.error || err.message, status: "error" });
      setDeployProdSteps(steps);
      toast.error("Deploy to prod failed");
    } finally {
      setDeployingProd(false);
    }
  };

  const handleRollback = async () => {
    if (!window.confirm("Are you sure you want to rollback production to the previous version?")) return;
    setRollingBack(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/deploy/prod/rollback`, {}, { withCredentials: true });
      setDeployProdSteps(res.data.steps || []);
      toast.success("Rolled back to previous version");
      fetchDeployPreview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rollback failed");
    } finally {
      setRollingBack(false);
    }
  };

  // ===== NEW UNIFIED BACKUP FUNCTIONS =====
  
  // Export ALL Data - Single JSON for all database collections
  const handleExportAllData = async () => {
    setExportingAll(true);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `dailylog_backup_${timestamp}.zip`;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/export-all`,
        { withCredentials: true, timeout: 300000, responseType: 'blob' }
      );

      // Parse metadata from response header
      let metadata = {};
      try {
        const metaHeader = response.headers['x-backup-metadata'];
        if (metaHeader) metadata = JSON.parse(metaHeader);
      } catch {}

      const collectionCounts = metadata.collection_counts || {};
      const exportStats = Object.entries(collectionCounts).map(([name, count]) => ({
        name,
        count: count || 0
      })).sort((a, b) => b.count - a.count);
      const totalRows = exportStats.reduce((sum, item) => sum + item.count, 0);

      const blob = response.data;

      // Try File System Access API first
      let saved = false;
      if ('showSaveFilePicker' in window) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{ description: 'ZIP Files', accept: { 'application/zip': ['.zip'] } }]
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          saved = true;
        } catch (err) {
          if (err.name === 'AbortError') {
            setExportingAll(false);
            return;
          }
        }
      }

      // Fallback download
      if (!saved) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
      }

      // Show export results dialog
      setExportResultData({
        type: 'export',
        filename,
        totalTables: exportStats.length,
        totalRows,
        collections: exportStats,
        createdAt: metadata.created_at
      });
      setExportResultDialogOpen(true);
      toast.success('Complete database backup exported successfully!');
    } catch (error) {
      console.error("Export ALL error:", error);
      toast.error(error.response?.data?.detail || "Failed to export data");
    } finally {
      setExportingAll(false);
    }
  };

  // Export ENV Data - Separate JSON for environment variables
  const handleExportEnvData = async () => {
    setExportingEnv(true);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `dailylog_env_backup_${timestamp}.json`;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/export-env`,
        { withCredentials: true }
      );
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      
      // Try File System Access API first
      if ('showSaveFilePicker' in window) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{ description: 'JSON Files', accept: { 'application/json': ['.json'] } }]
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          toast.success('Environment configuration exported successfully!');
          return;
        } catch (err) {
          if (err.name === 'AbortError') {
            setExportingEnv(false);
            return;
          }
        }
      }
      
      // Fallback download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success('Environment configuration exported!');
    } catch (error) {
      console.error("Export ENV error:", error);
      toast.error("Failed to export ENV");
    } finally {
      setExportingEnv(false);
    }
  };

  // Export FORMS - Separate JSON for forms only
  const handleExportForms = async () => {
    setExportingForms(true);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `dailylog_forms_${timestamp}.json`;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/export-forms`,
        { withCredentials: true }
      );
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Forms exported successfully! (${response.data.forms_count} forms)`);
    } catch (error) {
      console.error("Export FORMS error:", error);
      toast.error(error.response?.data?.detail || "Failed to export forms");
    } finally {
      setExportingForms(false);
    }
  };

  // Preview Import ALL Data
  const handleImportPreview = async () => {
    if (!importAllFile) {
      toast.error("Please select a backup file first");
      return;
    }
    setLoadingImportPreview(true);
    setImportPreview(null);
    try {
      const formData = new FormData();
      formData.append('file', importAllFile);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/import-all/preview`,
        formData,
        { withCredentials: true }
      );
      setImportPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate preview");
    } finally {
      setLoadingImportPreview(false);
    }
  };

  // Import ALL Data - with warning confirmation
  const handleImportAllData = async () => {
    if (!importAllFile) {
      toast.error("Please select a backup file first");
      return;
    }

    // Client-side validation
    const fileName = importAllFile.name.toLowerCase();
    if (!fileName.endsWith('.zip') && !fileName.endsWith('.json')) {
      toast.error(`Unsupported file type: "${importAllFile.name}". Please select a .zip or .json backup file.`);
      setImportAllResult({ success: false, message: `Unsupported file type: "${importAllFile.name}". Only .zip and .json backup files are supported.` });
      return;
    }

    if (importAllFile.size === 0) {
      toast.error("The selected file is empty (0 bytes). Please select a valid backup file.");
      setImportAllResult({ success: false, message: "The selected file is empty (0 bytes)." });
      return;
    }

    const fileSizeMB = (importAllFile.size / (1024 * 1024)).toFixed(1);

    // Show warning dialog if not already confirmed
    if (!showImportWarning) {
      setShowImportWarning(true);
      return;
    }

    setImportingAll(true);
    setImportAllResult(null);
    setImportProgress({ collection: "Uploading file...", percent: 0, index: 0, total: 0, records: 0 });

    try {
      const formData = new FormData();
      formData.append('file', importAllFile);

      // Use fetch for streaming response (axios doesn't support NDJSON streams)
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/import-all`,
        {
          method: 'POST',
          body: formData,
          credentials: 'include'
        }
      );

      if (!response.ok && response.headers.get('content-type')?.includes('application/json')) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Server error (${response.status})`);
      }

      if (!response.ok) {
        throw new Error(`Server error (${response.status}): ${response.statusText}`);
      }

      // Read NDJSON stream line by line
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalData = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep incomplete last line in buffer

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const event = JSON.parse(line);

            if (event.type === 'start') {
              setImportProgress({ collection: "Starting import...", percent: 0, index: 0, total: event.total, records: 0 });
            } else if (event.type === 'progress') {
              setImportProgress({
                collection: event.collection,
                percent: event.percent,
                index: event.index,
                total: event.total,
                records: event.records
              });
            } else if (event.type === 'complete') {
              finalData = event;
              setImportProgress({ collection: "Complete!", percent: 100, index: event.total_imported, total: event.total_imported, records: 0 });
            } else if (event.type === 'error') {
              throw new Error(event.message);
            }
          } catch (parseErr) {
            if (parseErr.message && !parseErr.message.includes('JSON')) throw parseErr;
            console.warn("Could not parse stream line:", line);
          }
        }
      }

      // Process remaining buffer
      if (buffer.trim()) {
        try {
          const event = JSON.parse(buffer);
          if (event.type === 'complete') finalData = event;
          if (event.type === 'error') throw new Error(event.message);
        } catch (e) {
          if (e.message && !e.message.includes('JSON')) throw e;
        }
      }

      if (!finalData) {
        throw new Error("Import stream ended without a completion event. The import may have been interrupted.");
      }

      // Build import stats for display
      const results = finalData.results || {};
      const importStats = Object.entries(results).map(([name, info]) => ({
        name,
        count: info.inserted || info.count || 0,
        success: info.success !== false,
        error: info.error || null
      })).sort((a, b) => b.count - a.count);

      const totalRows = importStats.reduce((sum, item) => sum + item.count, 0);
      const successCount = importStats.filter(i => i.success).length;
      const failedCollections = importStats.filter(i => !i.success);

      // Close import dialog and show results dialog
      setImportAllDialogOpen(false);
      setImportProgress(null);
      setExportResultData({
        type: 'import',
        totalTables: importStats.length,
        totalRows,
        successCount,
        collections: importStats,
        backupDate: finalData.backup_date,
        failedCount: finalData.failed_count || failedCollections.length,
        errors: finalData.errors || []
      });
      setExportResultDialogOpen(true);

      if (failedCollections.length > 0) {
        toast.warning(`Import completed with ${failedCollections.length} error(s): ${successCount}/${importStats.length} collections restored`);
      } else {
        toast.success(finalData.message || "Data imported successfully!");
      }

    } catch (error) {
      console.error("Import ALL error:", error);

      let errorMessage;
      if (error.name === 'TypeError' && error.message?.includes('fetch')) {
        errorMessage = "Cannot connect to the server. Please check that the backend is running on port 8001 and try again.";
      } else {
        errorMessage = error.message || `Import failed: ${error}`;
      }

      setImportAllResult({ success: false, message: errorMessage });
      setImportProgress(null);
      toast.error(errorMessage);
    } finally {
      setImportingAll(false);
    }
  };

  // Import FORMS handler
  const handleImportForms = async () => {
    if (!importFormsFile) {
      toast.error("Please select a forms file to import");
      return;
    }

    setImportingForms(true);
    try {
      const text = await importFormsFile.text();
      const formsData = JSON.parse(text);
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/import-forms`,
        formsData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      setImportFormsResult(response.data);
      toast.success(response.data.message || 'Forms imported successfully!');
    } catch (error) {
      console.error("Import FORMS error:", error);
      const errorMsg = error.response?.data?.detail || "Failed to import forms";
      toast.error(errorMsg);
      setImportFormsResult({ error: errorMsg });
    } finally {
      setImportingForms(false);
    }
  };

  // Backup Settings handlers
  const confirmImport = () => {
    handleImportAllData();
  };

  // ===== DATABASE MIGRATION FUNCTIONS =====
  
  // Export migration package (with schema + indexes + data)
  const handleExportMigration = async () => {
    setExportingMigration(true);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `dailylog_migration_package_${timestamp}.json`;
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/export-migration`,
        { withCredentials: true }
      );
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      
      // Try File System Access API first
      if ('showSaveFilePicker' in window) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{ description: 'JSON Files', accept: { 'application/json': ['.json'] } }]
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          toast.success('Migration package exported successfully!');
          setMigrationDialogOpen(false);
          return;
        } catch (err) {
          if (err.name === 'AbortError') {
            setExportingMigration(false);
            return;
          }
        }
      }
      
      // Fallback download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success('Migration package exported! Use this file to set up your new database.');
      setMigrationDialogOpen(false);
    } catch (error) {
      console.error("Export migration error:", error);
      toast.error(error.response?.data?.detail || "Failed to export migration package");
    } finally {
      setExportingMigration(false);
    }
  };
  
  // Test connection to new database
  const handleTestConnection = async () => {
    if (!newDbConnectionString.trim() || !newDbName.trim()) {
      toast.error("Please enter both connection string and database name");
      return;
    }
    
    setTestingConnection(true);
    setConnectionTestResult(null);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/test-connection`,
        {
          connection_string: newDbConnectionString,
          database_name: newDbName
        },
        { withCredentials: true }
      );
      
      setConnectionTestResult(response.data);
      if (response.data.success) {
        toast.success("Connection successful!");
      } else {
        toast.error(response.data.message || "Connection failed");
      }
    } catch (error) {
      console.error("Connection test error:", error);
      setConnectionTestResult({
        success: false,
        message: error.response?.data?.detail || "Connection test failed"
      });
      toast.error(error.response?.data?.detail || "Connection test failed");
    } finally {
      setTestingConnection(false);
    }
  };
  
  // Migrate data to new database
  const handleMigrateToNewDatabase = async () => {
    if (!migrationFile) {
      toast.error("Please select a migration package file");
      return;
    }
    
    if (!newDbConnectionString.trim() || !newDbName.trim()) {
      toast.error("Please enter connection details and test the connection first");
      return;
    }
    
    if (!connectionTestResult?.success) {
      toast.error("Please test the connection first and ensure it's successful");
      return;
    }
    
    setMigratingData(true);
    setMigrationResult(null);
    
    try {
      const fileContent = await migrationFile.text();
      const backupData = JSON.parse(fileContent);
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/migrate-to-new-database`,
        {
          connection_string: newDbConnectionString,
          database_name: newDbName,
          backup_data: backupData,
          drop_existing: true
        },
        { withCredentials: true }
      );
      
      setMigrationResult(response.data);
      
      if (response.data.success) {
        toast.success(response.data.message || "Migration completed successfully!");
      } else {
        toast.warning("Migration completed with some errors");
      }
    } catch (error) {
      console.error("Migration error:", error);
      if (error instanceof SyntaxError) {
        setMigrationResult({ success: false, message: "Invalid JSON file" });
        toast.error("Invalid migration package file");
      } else {
        setMigrationResult({ 
          success: false, 
          message: error.response?.data?.detail || "Migration failed" 
        });
        toast.error(error.response?.data?.detail || "Migration failed");
      }
    } finally {
      setMigratingData(false);
    }
  };
  
  const handleImportFromMP = async () => {
    if (!migrationFile) {
      toast.error("Please select a migration package file");
      return;
    }

    setImportingFromMP(true);
    setImportFromMPResult(null);

    try {
      // Upload file directly — let the server parse JSON (much faster than browser parsing)
      const formData = new FormData();
      formData.append("file", migrationFile);

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/backup/import-from-mp`,
        formData,
        {
          withCredentials: true,
          timeout: 600000, // 10 minutes for large packages
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      setImportFromMPResult(response.data);

      if (response.data.success) {
        toast.success(response.data.message || "Import completed successfully!");
      } else {
        toast.warning("Import completed with some errors");
      }
    } catch (error) {
      console.error("Import from MP error:", error);
      setImportFromMPResult({
        success: false,
        message: error.response?.data?.detail || error.message || "Import failed"
      });
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setImportingFromMP(false);
    }
  };

  // Reset migration dialog state
  const resetMigrationDialog = () => {
    setMigrationStep(1);
    setMigrationMode(null);
    setNewDbConnectionString('');
    setNewDbName('');
    setConnectionTestResult(null);
    setMigrationFile(null);
    setMigrationResult(null);
    setImportFromMPResult(null);
    if (migrationFileInputRef.current) migrationFileInputRef.current.value = '';
  };

  const [openSections, setOpenSections] = useState({ notifications: true, data: false, system: false });

  const toggleSection = (key) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpenSections(prev => ({ ...prev, [id]: true }));
    }
  };

  const SectionHeader = ({ id, icon: Icon, title, description, iconColor = "text-slate-600" }) => (
    <button
      onClick={() => toggleSection(id)}
      className="w-full flex items-center justify-between py-3 px-1 cursor-pointer hover:opacity-80 transition-opacity"
    >
      <div className="flex items-center gap-3">
        <div className={`p-1.5 rounded-md bg-slate-100`}>
          <Icon className={`h-5 w-5 ${iconColor}`} />
        </div>
        <div className="text-left">
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <p className="text-sm text-slate-500">{description}</p>
        </div>
      </div>
      <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${openSections[id] ? "rotate-180" : ""}`} />
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Settings className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
            <p className="text-slate-500">Configure system settings</p>
          </div>
        </div>
        {isTaj && (
          <Button
            size="sm"
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={openDeployDialog}
            disabled={deployingProd}
          >
            {deployingProd ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Rocket className="h-4 w-4 mr-1.5" />}
            Deploy to Prod
          </Button>
        )}
      </div>

      {/* Deploy to Prod Dialog */}
      <Dialog open={deployProdConfirmOpen} onOpenChange={(v) => { if (!deployingProd) { setDeployProdConfirmOpen(v); if (!v) setDeployProdSteps([]); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Rocket className="h-5 w-5" /> Deploy to Production
            </DialogTitle>
            <DialogDescription>
              Push current dev build to <strong>crm.aptask.com</strong>. Review changes below before deploying.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {/* Recent Deployments */}
          {deployPreview?.recent_deploys?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Recent Deployments</h4>
              {deployPreview.recent_deploys.map((d, i) => (
                <div key={i} className={`flex items-center justify-between p-2.5 rounded-lg border text-sm ${d.status === "success" ? "bg-green-50 border-green-200" : d.status === "rollback" ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200"}`}>
                  <div className="flex items-center gap-2">
                    {d.status === "success" ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> : d.status === "rollback" ? <RotateCcw className="h-3.5 w-3.5 text-amber-600" /> : <AlertTriangle className="h-3.5 w-3.5 text-red-600" />}
                    <div>
                      <span className="font-medium">{d.deployed_by}</span>
                      <span className="text-slate-500 ml-2">{new Date(d.timestamp + "Z").toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true })}</span>
                      {d.commit_short && <code className="ml-2 text-xs bg-slate-200 px-1.5 py-0.5 rounded">{d.commit_short}</code>}
                      <Badge variant="outline" className={`ml-2 text-xs ${d.status === "success" ? "text-green-700 border-green-300" : d.status === "rollback" ? "text-amber-700 border-amber-300" : "text-red-700 border-red-300"}`}>{d.status}</Badge>
                    </div>
                  </div>
                  {i === 0 && (
                    <Button variant="outline" size="sm" className="text-amber-700 border-amber-300 hover:bg-amber-50" onClick={handleRollback} disabled={rollingBack || deployingProd}>
                      {rollingBack ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RotateCcw className="h-3 w-3 mr-1" />}
                      Rollback
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Changes Preview */}
          {loadingPreviewDeploy ? (
            <div className="flex items-center justify-center py-6 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading changes...</div>
          ) : deployPreview && deployPreview.total_commits > 0 ? (
            <div className="space-y-3">
              {/* Summary Bar */}
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <GitCommit className="h-4 w-4 text-blue-600" />
                <span><strong>{deployPreview.total_commits}</strong> commit{deployPreview.total_commits !== 1 ? "s" : ""}</span>
                <span className="text-slate-400">|</span>
                <span><strong>{deployPreview.total_files}</strong> file{deployPreview.total_files !== 1 ? "s" : ""} changed</span>
                <span className="text-slate-400">|</span>
                <span><strong>{deployPreview.authors?.length}</strong> author{deployPreview.authors?.length !== 1 ? "s" : ""}</span>
                {deployPreview.has_backend_changes && <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Backend</Badge>}
                {deployPreview.has_frontend_changes && <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200">Frontend</Badge>}
              </div>

              {/* Commits by Author */}
              <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Changes by Author</h4>
              {deployPreview.authors?.map((author, ai) => (
                <div key={ai} className="border rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b">
                    <div className="flex items-center gap-2">
                      <div className="h-6 w-6 rounded-full bg-slate-300 flex items-center justify-center text-xs font-bold text-white">{author.name?.[0]?.toUpperCase()}</div>
                      <span className="font-medium text-sm">{author.name}</span>
                      <span className="text-xs text-slate-400">{author.email}</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">{author.commits.length} commit{author.commits.length !== 1 ? "s" : ""}</Badge>
                  </div>
                  <div className="divide-y">
                    {author.commits.map((c, ci) => (
                      <div key={ci} className="px-3 py-2 text-sm hover:bg-slate-50">
                        <div className="flex items-center gap-2">
                          <code className="text-xs text-slate-400 font-mono">{c.short_hash}</code>
                          <span className="flex-1 truncate">{c.message}</span>
                          <span className="text-xs text-slate-400 whitespace-nowrap">{new Date(c.date).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true })}</span>
                        </div>
                        {c.files.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {c.files.slice(0, 5).map((f, fi) => (
                              <span key={fi} className={`text-xs px-1.5 py-0.5 rounded ${f.status === "A" ? "bg-green-100 text-green-700" : f.status === "D" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}`}>
                                {f.path.split("/").pop()}
                              </span>
                            ))}
                            {c.files.length > 5 && <span className="text-xs text-slate-400">+{c.files.length - 5} more</span>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : deployPreview && deployPreview.total_commits === 0 ? (
            <div className="text-center py-6 text-slate-500"><CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-green-500" /> Production is up to date. No new changes to deploy.</div>
          ) : null}

          {/* Deploy Progress */}
          {deployProdSteps.length > 0 && (
            <div className="space-y-1 text-sm font-mono bg-slate-900 text-slate-100 rounded-lg p-3 max-h-48 overflow-y-auto">
              {deployProdSteps.map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  {s.status === "ok" ? <CheckCircle2 className="h-3.5 w-3.5 text-green-400 shrink-0" /> : s.status === "error" ? <AlertTriangle className="h-3.5 w-3.5 text-red-400 shrink-0" /> : s.status === "warn" ? <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0" /> : <Loader2 className="h-3.5 w-3.5 text-blue-400 animate-spin shrink-0" />}
                  <span>{s.message}</span>
                </div>
              ))}
            </div>
          )}

          </div>
          <DialogFooter className="gap-2 border-t pt-3 mt-2 shrink-0">
            <Button variant="outline" onClick={() => { setDeployProdConfirmOpen(false); setDeployProdSteps([]); }} disabled={deployingProd}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleDeployToProd} disabled={deployingProd || (deployPreview && deployPreview.total_commits === 0)}>
              {deployingProd ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Deploying...</> : <><Rocket className="h-4 w-4 mr-1.5" /> Deploy to Prod</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Navigation + Quick Actions */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border rounded-lg px-4 py-2 flex items-center justify-between gap-2 shadow-sm flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 mr-1">Jump to:</span>
          <Button variant="ghost" size="sm" className="text-sm" onClick={() => scrollToSection("notifications")}>
            <Bell className="h-3.5 w-3.5 mr-1.5" /> Notifications
          </Button>
          <Button variant="ghost" size="sm" className="text-sm" onClick={() => scrollToSection("data")}>
            <Database className="h-3.5 w-3.5 mr-1.5" /> Data
          </Button>
          <Button variant="ghost" size="sm" className="text-sm" onClick={() => scrollToSection("system")}>
            <Server className="h-3.5 w-3.5 mr-1.5" /> System
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="border-green-300 text-green-700 hover:bg-green-50"
            onClick={handleExportAllData}
            disabled={exportingAll}
          >
            {exportingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
            Export ALL
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-orange-300 text-orange-700 hover:bg-orange-50"
            onClick={() => {
              setImportAllFile(null);
              setImportAllResult(null);
              setOpenSections(prev => ({ ...prev, data: true }));
              setTimeout(() => setImportAllDialogOpen(true), 50);
            }}
          >
            <Upload className="h-3.5 w-3.5 mr-1.5" />
            Import ALL
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
            onClick={handleExportMigration}
            disabled={exportingMigration}
          >
            {exportingMigration ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Server className="h-3.5 w-3.5 mr-1.5" />}
            Migration
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeployModalOpen(true)}
          >
            <Rocket className="h-3.5 w-3.5 mr-1.5" />
            Deploy
          </Button>
        </div>
      </div>

      <DeployModal open={deployModalOpen} onOpenChange={setDeployModalOpen} />

      {/* ===== EMAIL PROVIDERS ===== */}
      <EmailProviderSettingsCard />

      {/* ===== NOTIFICATIONS & SCHEDULING ===== */}
      <div id="notifications" className="scroll-mt-16">
        <SectionHeader
          id="notifications"
          icon={Bell}
          title="Notifications & Scheduling"
          description="Reminders, holidays, email notifications, and reports"
          iconColor="text-blue-600"
        />
        {openSections.notifications && (
          <div className="space-y-4 mt-2">
            <ReminderSettingsCard
              todayReminderEnabled={todayReminderEnabled}
              setTodayReminderEnabled={setTodayReminderEnabled}
              finalReminderEnabled={finalReminderEnabled}
              setFinalReminderEnabled={setFinalReminderEnabled}
              todayReminderTime={todayReminderTime}
              setTodayReminderTime={setTodayReminderTime}
              finalReminderTime={finalReminderTime}
              setFinalReminderTime={setFinalReminderTime}
              savingReminders={savingReminders}
              handleSaveReminders={handleSaveReminders}
            />

            <HolidaySettingsCard />

            <EmailNotificationCard
              ccRemindersToTeamLeader={ccRemindersToTeamLeader}
              setCcRemindersToTeamLeader={setCcRemindersToTeamLeader}
              ccRemindersToAdmin={ccRemindersToAdmin}
              setCcRemindersToAdmin={setCcRemindersToAdmin}
              ccRemindersToTaj={ccRemindersToTaj}
              setCcRemindersToTaj={setCcRemindersToTaj}
              notifyTeamLeaderOnSubmission={notifyTeamLeaderOnSubmission}
              setNotifyTeamLeaderOnSubmission={setNotifyTeamLeaderOnSubmission}
              notifyAdminOnSubmission={notifyAdminOnSubmission}
              setNotifyAdminOnSubmission={setNotifyAdminOnSubmission}
              notifyTajOnSubmission={notifyTajOnSubmission}
              setNotifyTajOnSubmission={setNotifyTajOnSubmission}
              productivityThreshold={productivityThreshold}
              setProductivityThreshold={setProductivityThreshold}
              productivityAlertEmail={productivityAlertEmail}
              setProductivityAlertEmail={setProductivityAlertEmail}
              savingNotifications={savingNotifications}
              handleSaveNotifications={handleSaveNotifications}
            />

            <ManagerReportsCard
        teamLeaderReportEnabled={teamLeaderReportEnabled}
        setTeamLeaderReportEnabled={setTeamLeaderReportEnabled}
        teamLeaderReportTime={teamLeaderReportTime}
        setTeamLeaderReportTime={setTeamLeaderReportTime}
        includeTasksToday={includeTasksToday}
        setIncludeTasksToday={setIncludeTasksToday}
        includeChallenges={includeChallenges}
        setIncludeChallenges={setIncludeChallenges}
        includePlanTomorrow={includePlanTomorrow}
        setIncludePlanTomorrow={setIncludePlanTomorrow}
        includeAiDiscovery={includeAiDiscovery}
        setIncludeAiDiscovery={setIncludeAiDiscovery}
        savingTeamLeaderReport={savingTeamLeaderReport}
        handleSaveTeamLeaderReport={handleSaveTeamLeaderReport}
        testReportDialogOpen={testReportDialogOpen}
        setTestReportDialogOpen={setTestReportDialogOpen}
        teamLeaders={teamLeaders}
        selectedTeamLeaders={selectedTeamLeaders}
        loadingTeamLeaders={loadingTeamLeaders}
        generatingReport={generatingReport}
        sendReportToLeaders={sendReportToLeaders}
        setSendReportToLeaders={setSendReportToLeaders}
        openTestReportDialog={openTestReportDialog}
        toggleTeamLeaderSelection={toggleTeamLeaderSelection}
        selectAllTeamLeaders={selectAllTeamLeaders}
        deselectAllTeamLeaders={deselectAllTeamLeaders}
        handleGenerateTestReport={handleGenerateTestReport}
      />

            <CrmActivityReportCard
              enabled={crmActivityReportEnabled}
              setEnabled={setCrmActivityReportEnabled}
              recipients={crmActivityReportRecipients}
              saving={savingCrmActivityReport}
              onSave={handleSaveCrmActivityReport}
            />
          </div>
        )}
      </div>

      {/* ===== DATA MANAGEMENT ===== */}
      <div id="data" className="scroll-mt-16">
        <SectionHeader
          id="data"
          icon={Database}
          title="Data Management"
          description="Cleanup, backups, and data import/export"
          iconColor="text-emerald-600"
        />
        {openSections.data && (
          <div className="space-y-4 mt-2">
            <DataCleanupCard
        bulkDeleteDialogOpen={bulkDeleteDialogOpen}
        setBulkDeleteDialogOpen={setBulkDeleteDialogOpen}
        handleDeleteCompaniesWithNoLeads={handleDeleteCompaniesWithNoLeads}
        smartDeletePreview={smartDeletePreview}
        loadingSmartPreview={loadingSmartPreview}
        executingSmartDelete={executingSmartDelete}
        handleSmartDeletePreview={handleSmartDeletePreview}
        handleSmartDelete={handleSmartDelete}
        deleteConditions={deleteConditions}
        setDeleteConditions={setDeleteConditions}
        deleteCompaniesWithNoLeads={deleteCompaniesWithNoLeads}
        setDeleteCompaniesWithNoLeads={setDeleteCompaniesWithNoLeads}
        deletingLeads={deletingLeads}
        loadingPreview={loadingPreview}
        deletePreviewCount={deletePreviewCount}
        deletePreviewLeads={deletePreviewLeads}
        handlePreviewDelete={handlePreviewDelete}
        handleBulkDelete={handleBulkDelete}
        addCondition={addCondition}
        removeCondition={removeCondition}
        updateCondition={updateCondition}
        LEAD_FIELDS_FOR_FILTER={LEAD_FIELDS_FOR_FILTER}
        OPERATORS={OPERATORS}
        duplicateLeadsPreview={duplicateLeadsPreview}
        loadingDuplicateLeadsPreview={loadingDuplicateLeadsPreview}
        executingDuplicateLeadsDelete={executingDuplicateLeadsDelete}
        handleDuplicateLeadsPreview={handleDuplicateLeadsPreview}
        handleDuplicateLeadsDelete={handleDuplicateLeadsDelete}
        duplicateCompaniesPreview={duplicateCompaniesPreview}
        loadingDuplicateCompaniesPreview={loadingDuplicateCompaniesPreview}
        executingDuplicateCompaniesDelete={executingDuplicateCompaniesDelete}
        handleDuplicateCompaniesPreview={handleDuplicateCompaniesPreview}
        handleDuplicateCompaniesDelete={handleDuplicateCompaniesDelete}
        duplicateTemplatesPreview={duplicateTemplatesPreview}
        loadingDuplicateTemplatesPreview={loadingDuplicateTemplatesPreview}
        executingDuplicateTemplatesDelete={executingDuplicateTemplatesDelete}
        handleDuplicateTemplatesPreview={handleDuplicateTemplatesPreview}
        handleDuplicateTemplatesDelete={handleDuplicateTemplatesDelete}
        duplicateFormsPreview={duplicateFormsPreview}
        loadingDuplicateFormsPreview={loadingDuplicateFormsPreview}
        executingDuplicateFormsDelete={executingDuplicateFormsDelete}
        handleDuplicateFormsPreview={handleDuplicateFormsPreview}
        handleDuplicateFormsDelete={handleDuplicateFormsDelete}
      />

      {/* Ownership Sync */}
      <OwnershipSyncCard />

      {/* Lead Research (Bulk) */}
      <BulkResearchCard />

      {/* Lead Scoring */}
      <ScoringSettings />

      {/* Company Intelligence — Bulk Research */}
      <CompanyIntelligenceCard />

      {/* Backup & Restore (Selective, Sync, Complete, Automatic) */}
      <BackupRestoreCard
        selectiveExportDialogOpen={selectiveExportDialogOpen}
        setSelectiveExportDialogOpen={setSelectiveExportDialogOpen}
        selectiveImportDialogOpen={selectiveImportDialogOpen}
        setSelectiveImportDialogOpen={setSelectiveImportDialogOpen}
        syncDatabaseDialogOpen={syncDatabaseDialogOpen}
        setSyncDatabaseDialogOpen={setSyncDatabaseDialogOpen}
        exportingAll={exportingAll}
        setExportingAll={setExportingAll}
        exportingEnv={exportingEnv}
        setExportingEnv={setExportingEnv}
        importAllDialogOpen={importAllDialogOpen}
        setImportAllDialogOpen={setImportAllDialogOpen}
        importingAll={importingAll}
        setImportingAll={setImportingAll}
        importAllFile={importAllFile}
        setImportAllFile={setImportAllFile}
        importAllResult={importAllResult}
        setImportAllResult={setImportAllResult}
        importProgress={importProgress}
        showImportWarning={showImportWarning}
        setShowImportWarning={setShowImportWarning}
        exportResultDialogOpen={exportResultDialogOpen}
        setExportResultDialogOpen={setExportResultDialogOpen}
        exportResultData={exportResultData}
        setExportResultData={setExportResultData}
        importFormsDialogOpen={importFormsDialogOpen}
        setImportFormsDialogOpen={setImportFormsDialogOpen}
        importingForms={importingForms}
        setImportingForms={setImportingForms}
        importFormsFile={importFormsFile}
        setImportFormsFile={setImportFormsFile}
        importFormsResult={importFormsResult}
        setImportFormsResult={setImportFormsResult}
        exportingForms={exportingForms}
        setExportingForms={setExportingForms}
        exportDialogOpen={exportDialogOpen}
        setExportDialogOpen={setExportDialogOpen}
        importDialogOpen={importDialogOpen}
        setImportDialogOpen={setImportDialogOpen}
        exporting={exporting}
        setExporting={setExporting}
        importing={importing}
        setImporting={setImporting}
        selectedImportFiles={selectedImportFiles}
        setSelectedImportFiles={setSelectedImportFiles}
        importResult={importResult}
        setImportResult={setImportResult}
        combinedBackupFile={combinedBackupFile}
        setCombinedBackupFile={setCombinedBackupFile}
        selectedFolderName={selectedFolderName}
        setSelectedFolderName={setSelectedFolderName}
        folderFiles={folderFiles}
        setFolderFiles={setFolderFiles}
        combinedFileInputRef={combinedFileInputRef}
        crmExportDialogOpen={crmExportDialogOpen}
        setCrmExportDialogOpen={setCrmExportDialogOpen}
        crmImportDialogOpen={crmImportDialogOpen}
        setCrmImportDialogOpen={setCrmImportDialogOpen}
        crmExporting={crmExporting}
        setCrmExporting={setCrmExporting}
        crmImporting={crmImporting}
        setCrmImporting={setCrmImporting}
        crmBackupFile={crmBackupFile}
        setCrmBackupFile={setCrmBackupFile}
        crmImportResult={crmImportResult}
        setCrmImportResult={setCrmImportResult}
        crmFileInputRef={crmFileInputRef}
        backupSettings={backupSettings}
        setBackupSettings={setBackupSettings}
        backupSettingsLoading={backupSettingsLoading}
        savingBackupSettings={savingBackupSettings}
        backupList={backupList}
        runningBackup={runningBackup}
        handleSaveBackupSettings={handleSaveBackupSettings}
        handleRunBackupNow={handleRunBackupNow}
        handleDownloadBackup={handleDownloadBackup}
        handleDeleteBackup={handleDeleteBackup}
        fetchBackupSettings={fetchBackupSettings}
        formatFileSize={formatFileSize}
        handleExportAllData={handleExportAllData}
        handleExportEnvData={handleExportEnvData}
        handleExportForms={handleExportForms}
        handleImportAllData={handleImportAllData}
        handleImportPreview={handleImportPreview}
        importPreview={importPreview}
        setImportPreview={setImportPreview}
        loadingPreview={loadingImportPreview}
        handleImportFormsData={handleImportForms}
        handleExportCombined={handleExportCombined}
        handleExportAll={handleExportAll}
        handleExportTable={handleExportTable}
        handleImportCombined={handleImportCombined}
        handleImportFiles={handleImportFiles}
        handleFileSelect={handleFileSelect}
        clearSelectedFile={clearSelectedFile}
        handleCombinedFileSelect={handleCombinedFileSelect}
        handleSelectFolder={handleSelectFolder}
        handleExportCRM={handleCrmExport}
        handleImportCRM={handleCrmImport}
        selectedCollections={selectedCollections}
        setSelectedCollections={setSelectedCollections}
        exportingSelected={exportingSelected}
        setExportingSelected={setExportingSelected}
        importingSelected={importingSelected}
        setImportingSelected={setImportingSelected}
        selectiveImportFile={selectiveImportFile}
        setSelectiveImportFile={setSelectiveImportFile}
        selectiveImportResult={selectiveImportResult}
        setSelectiveImportResult={setSelectiveImportResult}
        selectiveImportFileRef={selectiveImportFileRef}
        handleSelectiveExport={handleExportSelected}
        handleSelectiveImport={handleImportSelected}
        toggleCollection={toggleCollection}
        selectAllCollections={selectAllCollections}
        deselectAllCollections={deselectAllCollections}
        AVAILABLE_COLLECTIONS={AVAILABLE_COLLECTIONS}
        syncMode={syncMode}
        setSyncMode={setSyncMode}
        syncConnectionString={syncConnectionString}
        setSyncConnectionString={setSyncConnectionString}
        syncDatabaseName={syncDatabaseName}
        setSyncDatabaseName={setSyncDatabaseName}
        testingSyncConnection={testingSyncConnection}
        setTestingSyncConnection={setTestingSyncConnection}
        syncConnectionTestResult={syncConnectionTestResult}
        setSyncConnectionTestResult={setSyncConnectionTestResult}
        syncingDatabase={syncingDatabase}
        setSyncingDatabase={setSyncingDatabase}
        syncResult={syncResult}
        setSyncResult={setSyncResult}
        handleTestSyncConnection={handleTestSyncConnection}
        handleSyncDatabase={handleSyncDatabase}
        fileInputRefs={fileInputRefs}
      />
          </div>
        )}
      </div>

      {/* ===== SYSTEM ===== */}
      <div id="system" className="scroll-mt-16">
        <SectionHeader
          id="system"
          icon={Server}
          title="System"
          description="Environment variables, database migration, and deployment"
          iconColor="text-purple-600"
        />
        {openSections.system && (
          <div className="space-y-4 mt-2">
            <AdvancedToolsCard
        handleOpenEnvVarsDialog={handleOpenEnvVarsDialog}
        migrationDialogOpen={migrationDialogOpen}
        setMigrationDialogOpen={setMigrationDialogOpen}
        migrationMode={migrationMode}
        setMigrationMode={setMigrationMode}
        migrationStep={migrationStep}
        setMigrationStep={setMigrationStep}
        exportingMigration={exportingMigration}
        setExportingMigration={setExportingMigration}
        newDbConnectionString={newDbConnectionString}
        setNewDbConnectionString={setNewDbConnectionString}
        newDbName={newDbName}
        setNewDbName={setNewDbName}
        testingConnection={testingConnection}
        setTestingConnection={setTestingConnection}
        connectionTestResult={connectionTestResult}
        setConnectionTestResult={setConnectionTestResult}
        migrationFile={migrationFile}
        setMigrationFile={setMigrationFile}
        migratingData={migratingData}
        setMigratingData={setMigratingData}
        migrationResult={migrationResult}
        setMigrationResult={setMigrationResult}
        migrationFileInputRef={migrationFileInputRef}
        resetMigrationDialog={resetMigrationDialog}
        handleExportMigration={handleExportMigration}
        handleTestConnection={handleTestConnection}
        handleImportMigration={handleMigrateToNewDatabase}
        importingFromMP={importingFromMP}
        setImportingFromMP={setImportingFromMP}
        importFromMPResult={importFromMPResult}
        setImportFromMPResult={setImportFromMPResult}
        handleImportFromMP={handleImportFromMP}
        envVarsDialogOpen={envVarsDialogOpen}
        setEnvVarsDialogOpen={setEnvVarsDialogOpen}
        envVars={envVars}
        editedEnvVars={editedEnvVars}
        loadingEnvVars={loadingEnvVars}
        savingEnvVars={savingEnvVars}
        activeEnvTab={activeEnvTab}
        setActiveEnvTab={setActiveEnvTab}
        handleEnvVarChange={handleEnvVarChange}
        handleSaveEnvVars={handleSaveEnvVars}
        hasEnvChanges={hasEnvChanges}
      />
          </div>
        )}
      </div>

    </div>
  );
}
